import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SCUIButton, SCUIDateRangerV2 } from '../../../../../atomic/atoms';
import { SCUIGeneralDrawer, SCUIGeneralDrawerHeader } from '../../../../../atomic/molecules';
import ShippingCommand from '../../../../../services/command/shipping/ShippingCommand.services';
import Swal from 'sweetalert2';
import { MPCFM } from '../../../mpCargoFeeManagement.classes';

const formatToDDMMYYYY = (val?: string | Date | null): string => {
    if (!val) return '';
    const d = val instanceof Date ? val : new Date(String(val));
    if (Number.isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
};

interface CargoCompanyOption {
    id: number;
    name: string;
}

interface FeeRowItem {
    id: number;
    shippingCompanyId: number;
    shippingCompanyName: string;
    desi: number | '';
    cost: number | '';
    startDate: string | Date | null;
    expirationDate: string | Date | null;
}

type Props = {
    open: boolean;
    onClose: () => void;
    companyOptions: CargoCompanyOption[];
    initialData?: FeeRowItem;
    setIsLoading: (isLoading: boolean) => void;
    onSaved: () => void;
    filterCompanyId: number;
};

const FeeManagementListDrawer = ({
    open,
    onClose,
    companyOptions,
    initialData,
    setIsLoading,
    onSaved,
    filterCompanyId,
}: Props) => {
    const { t } = useTranslation();

    const initialFormState: FeeRowItem = useMemo(
        () => ({
            id: 0,
            shippingCompanyId: filterCompanyId || 0,
            shippingCompanyName: '',
            desi: 0,
            cost: 0,
            startDate: formatToDDMMYYYY(new Date()),
            expirationDate: formatToDDMMYYYY(new Date()),
        }),
        []
    );

    const isEditMode = Boolean(initialData);

    const [formState, setFormState] = useState<FeeRowItem>(initialData || initialFormState);

    useEffect(() => {
        if (isEditMode && initialData) {
            const normalized: FeeRowItem = {
                id: initialData.id ?? 0,
                shippingCompanyId: filterCompanyId || 0,
                shippingCompanyName: initialData.shippingCompanyName ?? '',
                desi: initialData.desi ?? 0,
                cost: initialData.cost ?? 0,
                startDate: formatToDDMMYYYY(initialData.startDate) || formatToDDMMYYYY(new Date()),
                expirationDate: formatToDDMMYYYY(initialData.expirationDate) || formatToDDMMYYYY(new Date()),
            };
            setFormState(normalized);
        } else {
            setFormState(initialFormState);
        }
    }, [isEditMode, initialData, initialFormState]);

    const handleChangeCompany = (e: SelectChangeEvent<number>) => {
        const id = Number(e.target.value);
        const selected = companyOptions.find((x) => x.id === id);
        setFormState((prev) => ({ ...prev, shippingCompanyId: id, shippingCompanyName: selected?.name || '' }));
    };

    const handleChangeField = (key: keyof FeeRowItem, value: any) => {
        setFormState((prev) => ({ ...prev, [key]: value }));
    };

    const handleChangeStartEnd = (range: [string, string]) => {
        handleChangeField('startDate', range[0] || null);
        handleChangeField('expirationDate', range[1] || null);
    };

    const toDateObject = (val?: string | Date | null): Date | null => {
        if (!val) return null;
        if (val instanceof Date) return Number.isNaN(val.getTime()) ? null : val;

        const s = String(val).trim();
        if (!s) return null;

        // Common project formats: "DD-MM-YYYY" or "DD/MM/YYYY"
        const normalized = s.replace(/\./g, '-').replace(/\//g, '-');
        const parts = normalized.split('-');
        if (parts.length !== 3) {
            const d = new Date(s);
            return Number.isNaN(d.getTime()) ? null : d;
        }

        const [dd, mm, yyyy] = parts;
        const day = Number(dd);
        const month = Number(mm);
        const year = Number(yyyy);
        if (!day || !month || !year) return null;

        const d = new Date(year, month - 1, day);
        return Number.isNaN(d.getTime()) ? null : d;
    };

    const isAddModeValid = (): boolean => {
        const start = toDateObject(formState.startDate);
        const end = toDateObject(formState.expirationDate);
        const isSameDay =
            Boolean(start && end) &&
            start!.getFullYear() === end!.getFullYear() &&
            start!.getMonth() === end!.getMonth() &&
            start!.getDate() === end!.getDate();

        return (
            Number(formState.shippingCompanyId) > 0 &&
            Number(formState.desi) > -1 &&
            Number(formState.cost) > 0 &&
            !isSameDay
        );
    };

    const toMMDDYYYY = (val?: string | Date | null): string => {
        if (!val) return '';
        let d: Date;
        if (val instanceof Date) {
            d = val;
        } else {
            const s = String(val).trim();
            if (s.includes('T')) {
                d = new Date(s);
            } else {
                const parts = s.replace(/[.\\-]/g, '/').split('/');
                if (parts.length === 3) {
                    const [a, b, c] = parts.map(Number);
                    d = a > 31 ? new Date(a, b - 1, c) : new Date(c, b - 1, a);
                } else {
                    d = new Date(s);
                }
            }
        }
        if (Number.isNaN(d.getTime())) return '';
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const dy = String(d.getDate()).padStart(2, '0');
        const y = d.getFullYear();
        return `${m}/${dy}/${y}`;
    };

    const handleSave = () => {
        const payload = {
            shippingCompanyId: Number(formState.shippingCompanyId),
            desi: Number(formState.desi),
            cost: Number(formState.cost),
            startDate: toMMDDYYYY(formState.startDate),
            expirationDate: toMMDDYYYY(formState.expirationDate),
        };
        setIsLoading(true);
        ShippingCommand.UpsertSellerShipmentCosts(payload)
            .then((res: any) => {
                if (!res.isSuccess) {
                    Swal.fire({
                        title: t('general.error'),
                        text: res.message,
                        icon: 'error',
                    });
                    return;
                }
                Swal.fire({
                    title: t('general.success'),
                    icon: 'success',
                    timer: 1500,
                });
                onSaved();
                onClose();
                setFormState(initialFormState);
            })
            .catch(() => {
                Swal.fire({
                    title: t('general.error'),
                    text: t('general.anErrorOccured'),
                    icon: 'error',
                });
            })
            .finally(() => setIsLoading(false));
    };
    return (
        <SCUIGeneralDrawer width="480px" open={open} onClose={onClose}>
            <SCUIGeneralDrawerHeader
                header={
                    isEditMode
                        ? t('mpCargoFeeManagement.list.drawer.editTitle')
                        : t('mpCargoFeeManagement.list.drawer.addTitle')
                }
                onClose={onClose}
            />
            <Box className={MPCFM.feeDrawerBody} sx={{ padding: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box className={MPCFM.feeDrawerFields} sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    <FormControl className={MPCFM.feeDrawerFieldCompany} fullWidth>
                        <InputLabel sx={{ fontWeight: 600 }} id="cargo-company-label">
                            {t('mpCargoFeeManagement.list.drawer.cargoCompany')}
                        </InputLabel>
                        <Select
                            className={MPCFM.inputSelectDrawerCompany}
                            labelId="cargo-company-label"
                            value={initialData?.shippingCompanyId ?? formState?.shippingCompanyId ?? ''}
                            label={t('mpCargoFeeManagement.list.drawer.cargoCompany')}
                            onChange={handleChangeCompany as any}
                            disabled
                        >
                            {companyOptions
                                .filter((opt) => opt.id !== -1)
                                .map((opt) => (
                                    <MenuItem key={opt.id} value={opt.id}>
                                        {opt.name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>

                    <TextField
                        className={MPCFM.inputTextDrawerDesi}
                        label={t('mpCargoFeeManagement.list.drawer.desi')}
                        slotProps={{ inputLabel: { sx: { fontWeight: 600 } } }}
                        type="text"
                        value={formState?.desi ?? ''}
                        onChange={(e) => {
                            handleChangeField('desi', e.target.value);
                        }}
                        onKeyDown={(e: any) => {
                            const isDigit = e.key >= '0' && e.key <= '9';
                            const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];

                            if (!isDigit && !allowedKeys.includes(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        disabled={isEditMode}
                        fullWidth
                    />

                    <TextField
                        className={MPCFM.inputTextDrawerCost}
                        label={t('mpCargoFeeManagement.list.drawer.cost')}
                        slotProps={{
                            inputLabel: { sx: { fontWeight: 600 } },
                            htmlInput: {
                                inputMode: 'decimal',
                                pattern: '[0-9.]*',
                            },
                        }}
                        type="text"
                        value={formState?.cost ?? ''}
                        onChange={(e) => {
                            let v = String(e.target.value ?? '');
                            // Keep only digits and a single dot, and allow max 2 decimals
                            v = v.replace(/[^0-9.]/g, '');
                            const firstDot = v.indexOf('.');
                            if (firstDot !== -1) {
                                const intPart = v.slice(0, firstDot);
                                const decPart = v
                                    .slice(firstDot + 1)
                                    .replace(/\./g, '')
                                    .slice(0, 2);
                                v = `${intPart}.${decPart}`;
                            }
                            handleChangeField('cost', v);
                        }}
                        onKeyDown={(e: any) => {
                            const isDigit = e.key >= '0' && e.key <= '9';
                            const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', '.'];
                            if (e.key === '.' && e.target.value?.includes('.')) {
                                e.preventDefault();
                                return;
                            }
                            // If already has 2 decimals, block more digits
                            if (isDigit && String(e.target.value || '').includes('.')) {
                                const [, dec = ''] = String(e.target.value || '').split('.');
                                const selectionStart = e.target.selectionStart ?? 0;
                                const dotIndex = String(e.target.value || '').indexOf('.');
                                const isTypingInDecimals = selectionStart > dotIndex;
                                if (isTypingInDecimals && dec.length >= 2) {
                                    e.preventDefault();
                                    return;
                                }
                            }
                            if (!isDigit && !allowedKeys.includes(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        fullWidth
                    />

                    <Box className={MPCFM.feeDrawerDateSection}>
                        <Typography variant="subtitle2" sx={{ marginBottom: '16px', fontWeight: 600 }}>
                            {t('mpCargoFeeManagement.list.drawer.startEndDate')}
                        </Typography>
                        <Box className={MPCFM.feeDrawerDateRangeWrap}>
                            <SCUIDateRangerV2
                                fromDate={formState?.startDate}
                                toDate={formState?.expirationDate}
                                setDateRange={handleChangeStartEnd}
                                dateFormat="DD/MM/YYYY"
                                containerStyling={{ width: '100%' }}
                            />
                        </Box>
                    </Box>
                </Box>
                <Box
                    className={MPCFM.feeDrawerActionsRow}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '8px',
                        width: '100%',
                        paddingTop: '32px',
                    }}
                >
                    <SCUIButton
                        className={MPCFM.inputButtonDrawerClear}
                        text={t('general.clear')}
                        onClick={() =>
                            isEditMode ? setFormState(initialData || initialFormState) : setFormState(initialFormState)
                        }
                        buttonType="button"
                        type="primary"
                        sizeType="fixed"
                        size={'l'}
                        style={{ backgroundColor: 'red', borderColor: 'red' }}
                    />
                    <SCUIButton
                        className={MPCFM.inputButtonDrawerSave}
                        text={t('mpCargoFeeManagement.list.drawer.save')}
                        type="primary"
                        sizeType="fixed"
                        size={'l'}
                        buttonType="button"
                        onClick={handleSave}
                        disabled={!isEditMode && !isAddModeValid()}
                    />
                </Box>
            </Box>
        </SCUIGeneralDrawer>
    );
};

export default FeeManagementListDrawer;
